import "./ChatbotExamEvaluation.scss";
import {
  Avatar,
  Box,
  CircularProgress,
  Divider,
  Drawer,
  SvgIcon,
  Typography,
} from "@mui/material";
import { FunctionComponent, useEffect, useRef, useState } from "react";
import IconButtonField from "../Button/IconButtonField";
import { useAppDispatch, useAppSelector } from "../../Redux/Store/Hooks";
import TextInputFieldC from "../TextInput/TextInputFieldC";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { ReactComponent as Send } from "../../Assets/Images/sendicon.svg";
import CloseIcon from "@mui/icons-material/Close";
import Markdown from "react-markdown";
import rehypeKatex from "rehype-katex";
import remarkMath from "remark-math";
import "katex/dist/katex.min.css";
import {
  ChatbotExamEvaluation as chatChatbotExamEvaluationThunk,
  clearChatbotExamEvaluationStatus
} from "../../Redux/Slices/ChatbotExamEvaluation/ChatbotExamEvaluationSlicer";
import { ChatbotExamEvaluationProps, IChatbotExamEvaluation } from "../ChatbotExamEvaluation/IChatbotExamEvaluation";
import AvatarImage from "../../Assets/Images/Chatbot_Avatar.png";
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt';
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt';

const drawerWidth = 450;

const ChatDotsLoading = () => (
  <Box sx={{ display: "flex", alignItems: "center", gap: "6px", height: 20, pl: 1 }}>
    {[0, 1, 2].map((i) => (
      <Box
        key={i}
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: "#6b7280",
          animation: `dotBounce 1.2s infinite`,
          animationDelay: `${i * 0.2}s`,
        }}
      />
    ))}
    <style>
      {`
        @keyframes dotBounce {
          0%, 80%, 100% { transform: translateY(0); }
          40% { transform: translateY(-6px); }
        }
      `}
    </style>
  </Box>
);

const ChatChatbotExamEvaluation: FunctionComponent<ChatbotExamEvaluationProps> = (props) => {
  const dispatch = useAppDispatch();

  // Use correct slice for practice evaluation chat!
  const { data: chatbotData, isLoading: chatloading } = useAppSelector(
    (state) => state.ChatbotExamEvaluationSlicer
  );

  const { open, setOpen, student_exam_id } = props;
  const divRef = useRef<HTMLDivElement>(null);

  const methods = useForm<IChatbotExamEvaluation>();
  const formRef = useRef<HTMLFormElement>(null);

  // Store full chat (user + bot messages)
  const [chatHistory, setChatHistory] = useState<
    { sender: "user" | "bot"; message: string }[]
  >([]);

  // Clean ChatGPT HTML tags and normalize spacing
  const cleanMessage = (text = "") => {
    return text
      .replace(/<p><br><\/p>/g, "\n")
      .replace(/<br\s*\/?>/g, "\n")
      .replace(/<\/p>/g, "\n\n")
      .replace(/<p>/g, "")
      .replace(/<[^>]+>/g, "");
  };

  const onSubmit: SubmitHandler<IChatbotExamEvaluation> = (data: any) => {
    if (chatloading) return;

    const userMessage = data?.message;
    if (!userMessage?.trim()) return;

    // Format chat history for payload
    const chatContext = chatHistory
      .map(
        (chat) =>
          `${chat.sender === "user" ? "user" : "bot"}: ${cleanMessage(
            chat.message
          )}`
      )
      .join(", ");

    const payloadMessage = `Previous message:[ ${chatContext} ] , user asked message: ${userMessage}`;

    setChatHistory((prev) => [
      ...prev,
      { sender: "user", message: userMessage },
    ]);

    dispatch(
      chatChatbotExamEvaluationThunk({
        message: payloadMessage,
        student_exam_id: props.student_exam_id, // <-- Use prop here
      })
    );

    methods.reset({ message: "" });
  };

  useEffect(() => {
    if (
      chatbotData &&
      (typeof chatbotData === "string" || chatbotData?.message?.trim())
    ) {
      const botMessage =
        typeof chatbotData === "string"
          ? chatbotData
          : chatbotData.message || "";

      setChatHistory((prev) => [
        ...prev,
        { sender: "bot", message: botMessage },
      ]);
    }
  }, [chatbotData]);

  useEffect(() => {
    if (divRef.current) {
      divRef.current.scrollTop = divRef.current.scrollHeight;
    }
  }, [chatHistory]);

  return (
    <>
      <Drawer
        sx={{
          width: drawerWidth,
          marginTop: "60px",
          "& .MuiDrawer-paper": {
            padding: 1,
            width: drawerWidth,
            display: "flex",
            flexDirection: "column",
            zIndex: 100000,
          },
        }}
        variant="persistent"
        anchor="right"
        open={open}
      >
        <header className="chat_bot_headerss">
          <span
            style={{ fontSize: "13px", fontWeight: 600, marginTop: "15px" }}
          >
            Chat with Elliot
          </span>
          <IconButtonField
            sx={{ ml: "auto" }}
            onClick={() => {
              setOpen(false);
              dispatch(clearChatbotExamEvaluationStatus());
            }}
          >
            <CloseIcon />
          </IconButtonField>
        </header>

        <Divider />

        <div
          ref={divRef}
          className="message_area"
          style={{ flexGrow: 1, overflowY: "auto" }}
        >
          <Box sx={{ p: 2 }}>
            {/* Intro message */}
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
                mb: 1,
                display: "flex",
                gap: "10px",
                alignItems: "center",
              }}
            >
              <Avatar
                sx={{ bgcolor: "#009DFF", width: '20px', height: '20px' }}
                src={AvatarImage}
                alt="Profile"
                className="blinking-avatar2"
              />
              Elliot
            </Typography>
            <Box
              sx={{
                backgroundColor: "#f3f4f6",
                borderRadius: "12px",
                p: 2,
                mb: 2,
              }}
            >
              <Typography
                variant="body2"
                sx={{ lineHeight: 1.5, fontSize: "14px" }}
              >
                Hi! I'm Elliot, your AI study assistant. I'm here to help you with your studies and answer any questions you might have about your preparation!
              </Typography>
            </Box>
          </Box>

          <Box sx={{ px: 2, mb: 2 }}>
            {/* Suggestion buttons */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {["Study tips", "How can I improve?", "Practice recommendations", "General help"].map(
                (text, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      border: "1px solid #e0e0e0",
                      borderRadius: "5px",
                      px: 2,
                      py: 0.5,
                      fontSize: "13px",
                      fontWeight: "600",
                      cursor: chatloading ? "not-allowed" : "pointer", // <-- Change cursor
                      backgroundColor: chatloading ? "#f3f4f6" : "#fff", // <-- Change color
                      opacity: chatloading ? 0.6 : 1, // <-- Dim when loading
                      pointerEvents: chatloading ? "none" : "auto", // <-- Disable click
                      ":hover": {
                        backgroundColor: chatloading ? "#f3f4f6" : "#f1f5f9",
                      },
                    }}
                    onClick={() => {
                      if (chatloading) return;
                      setChatHistory((prev) => [
                        ...prev,
                        { sender: "user", message: text },
                      ]);
                      dispatch(
                        chatChatbotExamEvaluationThunk({
                          message: text,
                          student_exam_id: props.student_exam_id, // <-- Use prop here
                        })
                      );
                    }}
                  >
                    {text}
                  </Box>
                )
              )}
            </Box>
          </Box>

          {/* Mapped messages */}
          {chatHistory.map((entry, index) => (
            <Box
              key={index}
              sx={{
                mb: 2,
                display: "flex",
                flexDirection: "column",
                alignItems: entry.sender === "user" ? "flex-end" : "flex-start",
              }}
            >
              {entry.sender === "bot" && (
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                    display: "flex",
                    gap: "10px",
                    alignItems: "center",
                    justifyContent: "flex-start",
                  }}
                >
                  <Avatar
                    sx={{ bgcolor: "#009DFF", width: '20px', height: '20px' }}
                    src={AvatarImage}
                    alt="Profile"
                    className="blinking-avatar2"
                  />
                  Elliot
                </Typography>
              )}
              <Box
                sx={{
                  backgroundColor: entry.sender === "user" ? "" : "#f9fafb",
                  color: entry.sender === "user" ? "#fff" : "#333",
                  borderRadius: "16px",
                  padding: 1.5,
                  maxWidth: "85%",
                  fontSize: "15px",
                }}
              >
                <Markdown
                  children={entry.message}
                  remarkPlugins={[remarkMath]}
                  rehypePlugins={[rehypeKatex]}
                  className={
                    entry.sender === "user"
                      ? "user_message"
                      : "chat_bot_message"
                  }
                />
              </Box>
            </Box>
          ))}
          {chatloading && (
            <Box sx={{ display: "flex", alignItems: "center", px: 3, py: 1 }}>
              <ChatDotsLoading />
              <span style={{ marginLeft: 12, color: "#6b7280", fontSize: "15px" }}>
                Analyzing and responding... give us a moment
              </span>
            </Box>
          )}
        </div>

        <Divider />

        <footer className="chatbot_footer" style={{ padding: "0 16px 16px" }}>
          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)} ref={formRef}>
              <TextInputFieldC
                name="message"
                type="text"
                className="chat_input"
                size="small"
                fullWidth
                disabled={chatloading}
                placeholder={
                  chatloading
                    ? "Analyzing and responding... give us a moment"
                    : "Ask Elliot about any question or concept..."
                }
                InputProps={{
                  endAdornment: (
                    <IconButtonField
                      type="submit"
                      onClick={() => formRef.current?.requestSubmit()}
                      disabled={chatloading}
                      sx={{
                        pointerEvents: chatloading ? "none" : "auto", // Disable pointer events when loading
                        cursor: chatloading ? "not-allowed" : "pointer", // Show disabled cursor when loading
                        backgroundColor: chatloading ? "#f3f4f6" : "transparent", // Optional: visually indicate disabled
                        ":hover": {
                          backgroundColor: chatloading ? "#f3f4f6" : "#e3f2fd", // Prevent hover effect when loading
                        },
                      }}
                    >
                      <SvgIcon
                        component={Send}
                        sx={{
                          fontSize: "31px",
                          color: chatloading ? "#bdbdbd" : "#1976d2", // Dim icon when loading
                          pointerEvents: chatloading ? "none" : "auto", // Also disable pointer events on icon
                          cursor: chatloading ? "not-allowed" : "pointer", // Show disabled cursor on icon
                        }}
                        inheritViewBox
                      />
                    </IconButtonField>
                  ),
                  sx: {
                    borderRadius: "24px",
                    border: "2px solid #f3f4f6",
                    paddingLeft: chatloading ? "40px" : "16px",
                  },
                }}
              />
            </form>
          </FormProvider>
          <Typography sx={{ display: "flex", justifyContent: "center", color: "#6b7280" }}>
            Elliot is here to help with your studies.......
          </Typography>
        </footer>
      </Drawer>
    </>
  );
};

export default ChatChatbotExamEvaluation;